import { SafeHtml } from "~/hec/components/atoms/safe-html/safe-html";
import { FeatureLink } from "~/hec/components/molecules/feature-link/feature-link";
import { useFeatureLinkTarget } from "~/hec/hooks/use-feature-link-target";
import { useHeadingFontClass } from "~/hec/hooks/use-heading-font-class";
import {
  builderImageSrcSet,
  builderImageUrl,
  builderImageWidths,
} from "~/hec/utils/builder-image-url";
import { cn } from "~/lib/utils";

import type { FeaturedThreeUpProps, FeaturedTileProps } from "./types";

// Breakpoints are Builder's (small ≤639, medium ≤991), not Tailwind's, so the
// component lays out exactly like the symbol it replaces.
const WHITE_ARROW =
  "https://cdn.builder.io/api/v1/image/assets%2F1e07bd57fd054601b600816e06494849%2Fb4609ebfa90546d08e8a8becb18db0c6";
const DARK_ARROW =
  "https://cdn.builder.io/api/v1/image/assets%2F481825b100e64e7f9a22a215d86e03f1%2Fd587e995b1aa4a18ab533c5091ff4cfa";

// Three per row from 640px; a 4th tile wraps onto a centered row. The half
// pixel keeps three exact thirds from rounding past the row and wrapping.
const TILE_WIDTH = "min-[640px]:basis-[calc((100%_-_40px)/3_-_0.5px)]";

// Widest a tile gets: ~448px at the 1440px cap, the full card width below 640px.
const DESKTOP_WIDTHS = builderImageWidths(470);
const MOBILE_WIDTHS = builderImageWidths(390);

/** One tile: the whole tile is a link when it has a destination. */
function FeaturedTile({ item, index }: FeaturedTileProps) {
  const labelFont = useHeadingFontClass();
  const link = useFeatureLinkTarget(item.link);
  const image = item.desktopImage || item.mobileImage;

  return (
    <FeatureLink
      target={link}
      className={cn(
        "relative flex aspect-[3/2] flex-row gap-[4px] overflow-hidden rounded-[6px] px-[8px] pt-[8px]",
        "min-[640px]:aspect-[17/20] min-[640px]:grow-0 min-[640px]:gap-[8px] min-[640px]:px-[12px] min-[640px]:pt-[0px]",
        "min-[992px]:gap-[16px] min-[992px]:py-[8px]",
        TILE_WIDTH,
      )}
      data-testid={`featured-three-up-tile-${index}`}
    >
      {image ? (
        <picture>
          {item.mobileImage ? (
            <source
              media="(max-width: 639px)"
              sizes="calc(100vw - 52px)"
              srcSet={
                builderImageSrcSet(item.mobileImage, MOBILE_WIDTHS) ??
                item.mobileImage
              }
            />
          ) : null}
          <img
            src={builderImageUrl(image, { width: 940, format: "webp" })}
            srcSet={builderImageSrcSet(image, DESKTOP_WIDTHS)}
            // Tile widths: 1 column below 640, 3 columns above (padding grows
            // at 992), 448px once the section hits its 1440px cap.
            sizes="(max-width: 639px) calc(100vw - 52px), (max-width: 991px) calc((100vw - 92px) / 3), (max-width: 1455px) calc((100vw - 112px) / 3), 448px"
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </picture>
      ) : null}
      {item.whiteText ? (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[linear-gradient(180deg,rgba(30,30,30,0)_77.7%,rgba(30,30,30,0.5)_100%)]"
        />
      ) : null}
      {item.label ? (
        <SafeHtml
          html={item.label}
          className={cn(
            "min-[640px]:text-[16px] min-[992px]:mb-[0px] min-[992px]:text-[1.625rem] relative mb-[4px] mt-auto text-[18px] font-normal leading-[1.5]",
            labelFont,
            item.whiteText ? "text-white" : "text-[#142745]",
          )}
        />
      ) : null}
      <img
        src={item.whiteText ? WHITE_ARROW : DARK_ARROW}
        alt=""
        aria-hidden="true"
        className="min-[992px]:mb-[0px] min-[992px]:max-w-[23px] min-[992px]:py-[10px] relative mb-[8px] ml-auto mt-auto w-full max-w-[12px]"
      />
    </FeatureLink>
  );
}

/**
 * Organism: the "Featured 3-Up" symbol (COM-945) as a component. A card with a
 * heading over image tiles; each tile is one link. The label font is the
 * site's heading font (Volkolak Serif on HSA, Sans Display on FSA). Tile
 * labels arrive with no `<a>` (the block strips them): the tile is the link.
 */
export function FeaturedThreeUp({
  heading,
  items,
  className,
  ...rest
}: FeaturedThreeUpProps) {
  return (
    <section
      className={cn(
        "min-[640px]:px-[18px] min-[640px]:py-[28px] min-[992px]:p-[28px] relative mx-auto mb-[32px] flex w-[calc(100%_-_16px)] max-w-[1440px] flex-col overflow-hidden rounded-[12px] bg-white p-[18px] shadow-[0_4px_20px_0_rgba(0,0,0,0.22)]",
        className,
      )}
      data-testid="featured-three-up-section"
      {...rest}
    >
      {heading ? (
        <h2 className="min-[992px]:mb-[20px] min-[992px]:text-[36px] mb-[12px] text-balance text-center font-grotesque text-[26px] font-medium leading-[1.2] text-emphasis">
          {heading}
        </h2>
      ) : null}
      <div className="min-[640px]:flex-row min-[640px]:flex-wrap min-[640px]:justify-center min-[640px]:gap-[20px] flex flex-col gap-[16px]">
        {items.map((item, index) => (
          <FeaturedTile key={index} item={item} index={index} />
        ))}
      </div>
    </section>
  );
}
